import styled from '@emotion/styled';
import { WarningSharp } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { InfoAboutUpload, InfoAboutUploadRenderItem } from 'entities/users/types';
import { FC } from 'react';
import { ColumnConfigType } from 'shared/types';
import { DataTableFrontendPagination } from 'shared/ui/Table';
import { useGetCitiesQuery } from 'features/getCities/api/getCitiesApi';
import { mapInfoAboutUploadToRender } from '../utils/mappers';

const columnsKg: ColumnConfigType<InfoAboutUploadRenderItem>[] = [
  {
    dataFieldName: 'city',
    name: 'Город',
    width: '8%',
  },
  {
    dataFieldName: 'door_code',
    name: 'UID',
    width: '5%',
  },
  {
    dataFieldName: 'address',
    name: 'Контрагент',
    width: '15%',
  },
  {
    dataFieldName: 'client_number',
    name: 'Номер клиента',
    width: '8%',
  },
  {
    dataFieldName: 'iin',
    name: 'ИНН',
    width: '10%',
  },
  {
    dataFieldName: 'fio',
    name: 'ФИО',
    width: '15%',
  },
  {
    dataFieldName: 'passport_number',
    name: 'Номер паспорта',
    width: '8%',
  },
  {
    dataFieldName: 'date_start',
    name: 'Дата выдачи',
    width: '8%',
  },
  {
    dataFieldName: 'date_end',
    name: 'Срок истечения',
    width: '8%',
  },
  {
    dataFieldName: 'old_phone',
    name: 'Старый номер',
    width: '8%',
  },
  {
    dataFieldName: 'bonuses_amount',
    name: 'Бонусы',
    width: '8%',
  },
];

const columnsKz: ColumnConfigType<InfoAboutUploadRenderItem>[] = [
  {
    dataFieldName: 'city',
    name: 'Город',
    width: '8%',
  },
  {
    dataFieldName: 'door_code',
    name: 'UID',
    width: '5%',
  },
  {
    dataFieldName: 'address',
    name: 'Контрагент',
    width: '15%',
  },
  {
    dataFieldName: 'client_number',
    name: 'Номер клиента',
    width: '8%',
  },
  {
    dataFieldName: 'iin',
    name: 'ИИН',
    width: '10%',
  },
  {
    dataFieldName: 'fio',
    name: 'ФИО',
    width: '15%',
  },
  {
    dataFieldName: 'old_phone',
    name: 'Старый номер',
    width: '8%',
  },
  {
    dataFieldName: 'bonuses_amount',
    name: 'Бонусы',
    width: '8%',
  },
];

const columnsUZ: ColumnConfigType<InfoAboutUploadRenderItem>[] = [
  {
    dataFieldName: 'city',
    name: 'Город',
    width: '8%',
  },
  {
    dataFieldName: 'door_code',
    name: 'UID',
    width: '5%',
  },
  {
    dataFieldName: 'title',
    name: 'Контрагент',
    width: '15%',
  },
  {
    dataFieldName: 'client_number',
    name: 'Номер клиента',
    width: '8%',
  },
  {
    dataFieldName: 'iin',
    name: 'ИИН',
    width: '10%',
  },
  {
    dataFieldName: 'fio',
    name: 'ФИО',
    width: '15%',
  },
  {
    dataFieldName: 'passport_number',
    name: 'Номер паспорта',
    width: '8%',
  },
  {
    dataFieldName: 'date_start',
    name: 'Дата выдачи',
    width: '8%',
  },
  {
    dataFieldName: 'date_end',
    name: 'Срок истечения',
    width: '8%',
  },
  {
    dataFieldName: 'old_phone',
    name: 'Старый номер',
    width: '8%',
  },
  {
    dataFieldName: 'bonuses_amount',
    name: 'Бонусы',
    width: '8%',
  },
];

type Props = {
  infoAboutUpload: InfoAboutUpload;
  countryId: number;
};

const ShopDotsUpdateUsersInfoWidget: FC<Props> = ({ infoAboutUpload, countryId }) => {
  const { data: citiesList } = useGetCitiesQuery({});
  const loadedRender = infoAboutUpload.loaded.split('/');

  const getCountryColumns = (countryId: number): ColumnConfigType<InfoAboutUploadRenderItem>[] => {
    switch (countryId) {
      case 1:
        return columnsKz;
      case 3:
        return columnsKg;
      case 4:
        return columnsUZ;
      default:
        return [];
    }
  };

  const columns = getCountryColumns(countryId);

  const handleClickDownloadFile = (type: 'all' | 'errors') => {
    const { results, errors } = infoAboutUpload;
    const a = document.createElement('a');
    a.href = type === 'all' ? results : errors;
    a.download = type === 'all' ? 'LoadedUsers.xlsx' : 'NotLoadedUsers.xlsx';
    document.body.appendChild(a);
    a.target = '_blank';
    a.click();
    window.URL.revokeObjectURL(type === 'all' ? results : errors);
  };

  return (
    <>
      <Heading style={{ marginBottom: '16px' }}>Информация о загрузке</Heading>
      <Text>
        Загружено: {loadedRender[0]} из {loadedRender[1]}
      </Text>
      {infoAboutUpload.errors && (
        <Box
          display="flex"
          alignItems="center"
        >
          <Box mt="0px">
            <WarningSharp color="error" />
          </Box>
          <Box
            mt="2px"
            ml="4px"
          >
            <Text>Ошибки: </Text>
          </Box>
          <Box mt="4px">
            <Button
              variant="text"
              onClick={() => handleClickDownloadFile('errors')}
            >
              Скачать EXCEL
            </Button>
          </Box>
        </Box>
      )}
      {infoAboutUpload.items && citiesList && (
        <>
          <Box
            display="flex"
            alignItems="center"
            mt={3}
          >
            <Box mr={2}>
              <Text>Загруженные данные</Text>
            </Box>
            <Button
              onClick={() => handleClickDownloadFile('all')}
              variant="contained"
              disabled={infoAboutUpload.items.length === 0}
              size="small"
            >
              Скачать
            </Button>
          </Box>
          <DataTableFrontendPagination
            data={mapInfoAboutUploadToRender(infoAboutUpload.items, citiesList)}
            title=""
            columnConfig={columns}
          />
        </>
      )}
    </>
  );
};

const Heading = styled.h5`
  font-family: 'Inter';
  font-size: 24px;
  font-weight: 400;
  margin-top: 20px;
  margin-bottom: 8px;
`;

const Text = styled.span`
  font-family: 'Inter';
  font-size: 16px;
`;

export default ShopDotsUpdateUsersInfoWidget;
